import { useState } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

const Upgrade = ({ onBack }) => {
  const { user, profile, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isPaid = profile?.tier === "paid";

  const handleUpgrade = async () => {
    if (!user) {
      signInWithGoogle();
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch("/api/create-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          userEmail: user.email,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "Something went wrong. Please try again.");
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        setError("Something went wrong. Please try again.");
        setLoading(false);
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section
      style={{
        padding: "clamp(64px, 10vh, 120px) 24px",
        maxWidth: "800px", margin: "0 auto",
      }}
      aria-labelledby="upgrade-heading"
    >
      <button
        onClick={onBack}
        aria-label="Go back to home page"
        style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: "14px", color: "var(--ink-muted)", fontFamily: "inherit",
          marginBottom: "32px", padding: 0,
        }}
        onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
        onMouseLeave={(e) => e.target.style.color = "var(--ink-muted)"}
      >
        &larr; Back
      </button>

      <h1 id="upgrade-heading" style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: "clamp(28px, 4vw, 36px)", fontWeight: "400",
        color: "var(--ink)", letterSpacing: "-0.02em",
        marginBottom: "12px",
      }}>
        {isPaid ? "You're on the Pro plan" : "Get more from Ask Irene"}
      </h1>

      <p style={{
        fontSize: "16px", color: "var(--ink-soft)", lineHeight: 1.7,
        marginBottom: "40px", maxWidth: "560px",
      }}>
        {isPaid
          ? "Thank you for supporting Ask Irene. You have full access to all Pro features."
          : "The free chat advisor will always be free. Pro gives you the tools to take action on your bills."
        }
      </p>

      {/* Pricing cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "20px", marginBottom: "40px",
      }}>
        {/* Free tier */}
        <div style={{
          backgroundColor: "#fff", borderRadius: "12px",
          border: "1px solid var(--border)", padding: "28px",
        }}>
          <p style={{
            fontSize: "13px", color: "var(--ink-muted)", textTransform: "uppercase",
            letterSpacing: "0.06em", marginBottom: "8px",
          }}>Free</p>
          <p style={{
            fontSize: "32px", fontWeight: "600", color: "var(--ink)",
            fontFamily: "'DM Serif Display', serif", marginBottom: "4px",
          }}>$0</p>
          <p style={{
            fontSize: "14px", color: "var(--ink-muted)", marginBottom: "24px",
          }}>Always free</p>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[
              "Chat with Irene about any billing question",
              "Local resource search powered by AI",
              "Guidance on EOBs, disputes, and appeals",
              "Tips for negotiating bills and payment plans",
              "Cancer Cost Survival Guide",
            ].map((feature, i) => (
              <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <span style={{ color: "var(--accent)", fontSize: "16px", lineHeight: "24px", flexShrink: 0 }}>&#10003;</span>
                <span style={{ fontSize: "14px", color: "var(--ink-soft)", lineHeight: "24px" }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pro tier */}
        <div style={{
          backgroundColor: "#fff", borderRadius: "12px",
          border: isPaid ? "2px solid var(--accent)" : "2px solid var(--accent)",
          padding: "28px", position: "relative",
        }}>
          {isPaid && (
            <span style={{
              position: "absolute", top: "-12px", left: "20px",
              backgroundColor: "var(--accent)", color: "#fff",
              fontSize: "11px", fontWeight: "600", padding: "4px 12px",
              borderRadius: "12px", textTransform: "uppercase", letterSpacing: "0.06em",
            }}>Your plan</span>
          )}
          <p style={{
            fontSize: "13px", color: "var(--accent)", textTransform: "uppercase",
            letterSpacing: "0.06em", marginBottom: "8px", fontWeight: "600",
          }}>Pro</p>
          <div style={{ display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "4px" }}>
            <span style={{
              fontSize: "32px", fontWeight: "600", color: "var(--ink)",
              fontFamily: "'DM Serif Display', serif",
            }}>$9</span>
            <span style={{ fontSize: "14px", color: "var(--ink-muted)" }}>/month</span>
          </div>
          <p style={{
            fontSize: "14px", color: "var(--ink-muted)", marginBottom: "24px",
          }}>Cancel anytime</p>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "28px" }}>
            {[
              "Everything in Free",
              "Upload bills for AI-powered analysis",
              "Line-by-line error detection on your bills",
              "Conversation history — pick up where you left off",
              "Delete your saved history anytime",
            ].map((feature, i) => (
              <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <span style={{ color: "var(--accent)", fontSize: "16px", lineHeight: "24px", flexShrink: 0 }}>&#10003;</span>
                <span style={{ fontSize: "14px", color: "var(--ink-soft)", lineHeight: "24px" }}>{feature}</span>
              </div>
            ))}
          </div>

          {!isPaid && (
            <p style={{
              fontSize: "13px", color: "var(--ink-soft)", lineHeight: 1.6,
              marginBottom: "12px", textAlign: "center",
            }}>
              $9/month plus applicable tax. Renews automatically each month until you
              cancel. Cancel anytime online from your Account page.
            </p>
          )}

          {!isPaid && (
            <button
              onClick={handleUpgrade}
              disabled={loading}
              style={{
                width: "100%", padding: "14px", fontSize: "15px", fontWeight: "600",
                backgroundColor: loading ? "var(--ink-muted)" : "var(--accent)",
                color: "#fff",
                border: "none", borderRadius: "8px", cursor: loading ? "default" : "pointer",
                fontFamily: "inherit", transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => { if (!loading) e.target.style.backgroundColor = "var(--accent-light)"; }}
              onMouseLeave={(e) => { if (!loading) e.target.style.backgroundColor = "var(--accent)"; }}
            >
              {loading ? "Redirecting to checkout..." : user ? "Upgrade to Pro" : "Sign in to upgrade"}
            </button>
          )}

          {error && (
            <p style={{
              fontSize: "13px", color: "#c44", marginTop: "12px", textAlign: "center",
            }} role="alert">{error}</p>
          )}
        </div>
      </div>

      <p style={{
        fontSize: "12px", color: "var(--ink-muted)", textAlign: "center",
        maxWidth: "480px", margin: "0 auto",
      }}>
        Payments are processed securely by Stripe. You can cancel your subscription at any time from your account page.
      </p>
    </section>
  );
};

export default Upgrade;
